import { z } from 'zod'
import {
  PeripheralType,
  type PrinterPeripheral,
  type ScalePeripheral,
  type SatPeripheral,
  type Peripheral
} from '../../models/Peripheral'
import {
  PeripheralPrinterSettingsDTO,
  mapPeripheralPrinterSettingsToDTO,
  mapDTOToPeripheralPrinterSettings
} from './PeripheralPrinterSettingsDTO'
import {
  PeripheralScaleSettingsDTO,
  mapPeripheralScaleSettingsToDTO,
  mapDTOToPeripheralScaleSettings
} from './PeripheralScaleSettingsDTO'
import {
  PeripheralSatSettingsDTO,
  mapPeripheralSatSettingsToDTO,
  mapDTOToPeripheralSatSettings
} from './PeripheralSatSettingsDTO'
import { SlimModelDTO } from '~/domain/shared/slim/gateways/DTOs/SlimModelDTO'

export const PeripheralServerDTO = z.object({
  ip: z.string().nullish(),
  port: z.number().nullish()
})

// Schema base para periféricos que estende SlimModelDTO
export const BasePeripheralDTO = SlimModelDTO.extend({
  manufacturer: z.string().nullish(),
  model: z.string().nullish(),
  server: PeripheralServerDTO.nullish()
})

// DTOs específicos para cada tipo de periférico
const PrinterPeripheralDTO = BasePeripheralDTO.extend({
  type: z.enum([PeripheralType.Printer, PeripheralType.LabelPrinter]),
  settings: PeripheralPrinterSettingsDTO.nullish()
})

const ScalePeripheralDTO = BasePeripheralDTO.extend({
  type: z.enum([PeripheralType.Scale]),
  settings: PeripheralScaleSettingsDTO.nullish()
})

const SatPeripheralDTO = BasePeripheralDTO.extend({
  type: z.enum([PeripheralType.Sat]),
  settings: PeripheralSatSettingsDTO.nullish()
})

// Union discriminada
export const PeripheralDTO = z.discriminatedUnion('type', [PrinterPeripheralDTO, ScalePeripheralDTO, SatPeripheralDTO])

export const PeripheralSlimDTO = BasePeripheralDTO.extend({
  id: z.number(),
  type: z.string()
})

export type BasePeripheralDTO = z.infer<typeof BasePeripheralDTO>
export type PeripheralSlimDTO = z.infer<typeof PeripheralSlimDTO>
export type PeripheralDTO = z.infer<typeof PeripheralDTO>
export type PrinterPeripheralDTO = z.infer<typeof PrinterPeripheralDTO>
export type ScalePeripheralDTO = z.infer<typeof ScalePeripheralDTO>
export type SatPeripheralDTO = z.infer<typeof SatPeripheralDTO>

export function mapPeripheralToDTO (model: Peripheral): PeripheralDTO {
  const baseMapping = {
    id: model.id,
    name: model.name,
    manufacturer: model.manufacturer,
    model: model.description,
    server: model.server
  }

  switch (model.type) {
    case PeripheralType.Printer:
    case PeripheralType.LabelPrinter:
      return {
        ...baseMapping,
        type: model.type,
        settings: model.settings ? mapPeripheralPrinterSettingsToDTO(model.settings) : undefined
      }
    case PeripheralType.Scale:
      return {
        ...baseMapping,
        type: model.type,
        settings: model.settings ? mapPeripheralScaleSettingsToDTO(model.settings) : undefined
      }
    case PeripheralType.Sat:
      return {
        ...baseMapping,
        type: model.type,
        settings: model.settings ? mapPeripheralSatSettingsToDTO(model.settings) : undefined
      }
  }
}

export function mapDTOToPeripheralFromSlim (dto: PeripheralSlimDTO): Peripheral {
  return {
    id: dto.id,
    name: dto.name,
    type: dto.type as PeripheralType
  } as Peripheral
}

export function mapDTOToPeripheral (dto: PeripheralDTO): Peripheral {
  const baseMapping = {
    id: dto.id,
    name: dto.name,
    manufacturer: dto.manufacturer,
    description: dto.model,
    server: dto.server
      ? {
          ip: dto.server.ip,
          port: dto.server.port
        }
      : undefined
  }

  switch (dto.type) {
    case PeripheralType.Printer:
    case PeripheralType.LabelPrinter:
      return {
        ...baseMapping,
        type: dto.type,
        settings: dto.settings ? mapDTOToPeripheralPrinterSettings(dto.settings) : undefined
      } as PrinterPeripheral
    case PeripheralType.Scale:
      return {
        ...baseMapping,
        type: dto.type,
        settings: dto.settings ? mapDTOToPeripheralScaleSettings(dto.settings) : undefined
      } as ScalePeripheral
    case PeripheralType.Sat:
      return {
        ...baseMapping,
        type: dto.type,
        settings: dto.settings ? mapDTOToPeripheralSatSettings(dto.settings) : undefined
      } as SatPeripheral
  }
}
